import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { NavBar } from '../../components/NavBar';
import { api } from '../../lib/api';
import type { Home } from '@home-guard/shared';

interface PaintColor {
  id: string;
  home_id: string;
  room: string;
  color_name: string | null;
  color_code: string | null;
  brand: string | null;
  finish: string | null;
  notes: string | null;
  painted_at: string | null;
  created_at: string;
  updated_at: string;
}

type Tab = 'all' | 'interior' | 'exterior';

const INTERIOR_ROOMS = ['living room','bedroom','kitchen','bathroom','dining room','hallway','office','basement','laundry'];
const FINISH_OPTS = ['flat','eggshell','satin','semi_gloss','gloss'] as const;

const addSchema = z.object({
  room:       z.string().min(1,'Room is required').max(100).trim(),
  color_name: z.string().max(100).trim().optional(),
  color_code: z.string().max(20).trim().optional(),
  brand:      z.string().max(100).trim().optional(),
  finish:     z.enum(['flat','eggshell','satin','semi_gloss','gloss','']).optional(),
  painted_at: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().or(z.literal('')),
  notes:      z.string().max(1000).trim().optional(),
});

type AddForm = z.infer<typeof addSchema>;

function isInterior(room: string): boolean {
  return INTERIOR_ROOMS.some(r => room.toLowerCase().includes(r));
}

function hexToDisplay(hex: string | null): string {
  if (!hex) return '#e5e7eb';
  return hex.startsWith('#') ? hex : `#${hex}`;
}

export function PaintColors() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('all');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<PaintColor | null>(null);

  const { data: homesData } = useQuery({
    queryKey: ['homes'],
    queryFn: () => api.get<{ data: Home[] }>('/homes'),
  });
  const homes = homesData?.data ?? [];
  const home = homes.find(h => h.is_primary) ?? homes[0];

  const { data: colorsData, isLoading } = useQuery({
    queryKey: ['paint-colors', home?.id ?? ''],
    queryFn: () => api.get<{ data: PaintColor[] }>(`/homes/${home!.id}/paint-colors`),
    enabled: !!home,
  });

  const colors = colorsData?.data ?? [];
  const filtered = tab === 'all' ? colors
    : tab === 'interior' ? colors.filter(c => isInterior(c.room))
    : colors.filter(c => !isInterior(c.room));

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete<{ success: boolean }>(`/paint-colors/${id}`),
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: ['paint-colors'] }); },
  });

  const tabs: { key: Tab; label: string }[] = [
    { key: 'all',      label: 'All'      },
    { key: 'interior', label: 'Interior' },
    { key: 'exterior', label: 'Exterior' },
  ];

  return (
    <div className="min-h-dvh bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="flex items-center justify-between px-4 pt-12 pb-3">
          <button onClick={() => navigate('/app/profile')} className="flex items-center gap-1 text-gray-600 text-sm font-medium" style={{ minHeight: 44 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Profile
          </button>
          <h1 className="text-base font-bold text-gray-900">Paint Colors</h1>
          <button
            onClick={() => { setEditing(null); setShowModal(true); }}
            className="text-blue-600 text-sm font-bold"
            style={{ minHeight: 44 }}
          >
            + Add
          </button>
        </div>

        {/* Filter tabs */}
        <div className="flex px-4 gap-1 pb-0">
          {tabs.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-all ${
                tab === t.key
                  ? 'bg-gray-50 text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500'
              }`}
              style={{ minHeight: 40 }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-4 pt-4">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <div className="w-7 h-7 border-[3px] border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl p-8 text-center shadow-sm mt-2">
            <p className="text-4xl mb-3">🎨</p>
            <p className="font-semibold text-gray-800 mb-1">No paint colors yet</p>
            <p className="text-sm text-gray-500 mb-4">
              {tab === 'all'
                ? 'Track your paint colors so you can touch up perfectly later.'
                : `No ${tab} colors added yet.`}
            </p>
            <button
              onClick={() => { setEditing(null); setShowModal(true); }}
              className="px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold"
              style={{ minHeight: 44 }}
            >
              Add first color
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm divide-y divide-gray-100">
            {filtered.map(color => (
              <div key={color.id} className="flex items-center gap-3 px-4 py-3.5">
                {/* Color swatch */}
                <div
                  className="w-10 h-10 rounded-full flex-shrink-0 border border-gray-200"
                  style={{ background: hexToDisplay(color.color_code) }}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{color.room}</p>
                  <p className="text-xs text-gray-500 truncate">
                    {[color.color_name, color.brand, color.finish ? color.finish.replace(/_/g,' ') : null]
                      .filter(Boolean).join(' · ')}
                  </p>
                  {color.painted_at && (
                    <p className="text-xs text-gray-400 mt-0.5">Painted {color.painted_at}</p>
                  )}
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button
                    onClick={() => { setEditing(color); setShowModal(true); }}
                    className="p-2 text-gray-400 rounded-lg"
                    style={{ minHeight: 44, minWidth: 44 }}
                    aria-label="Edit"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm(`Remove ${color.room}?`)) deleteMutation.mutate(color.id);
                    }}
                    className="p-2 text-gray-400 rounded-lg"
                    style={{ minHeight: 44, minWidth: 44 }}
                    aria-label="Delete"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6m4-6v6" /><path d="M9 6V4h6v2" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && home && (
        <PaintModal
          homeId={home.id}
          existing={editing}
          onClose={() => { setShowModal(false); setEditing(null); }}
          onSuccess={() => {
            setShowModal(false);
            setEditing(null);
            void queryClient.invalidateQueries({ queryKey: ['paint-colors'] });
          }}
        />
      )}

      <NavBar />
    </div>
  );
}

function PaintModal({
  homeId, existing, onClose, onSuccess,
}: {
  homeId: string;
  existing: PaintColor | null;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const {
    register, handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<AddForm>({
    resolver: zodResolver(addSchema),
    defaultValues: {
      room:       existing?.room ?? '',
      color_name: existing?.color_name ?? '',
      color_code: existing?.color_code ?? '',
      brand:      existing?.brand ?? '',
      finish:     (existing?.finish ?? '') as AddForm['finish'],
      painted_at: existing?.painted_at ?? '',
      notes:      existing?.notes ?? '',
    },
  });

  const onSubmit = async (data: AddForm) => {
    try {
      const payload = {
        room: data.room,
        ...(data.color_name ? { color_name: data.color_name } : {}),
        ...(data.color_code  ? { color_code:  data.color_code  } : {}),
        ...(data.brand       ? { brand:       data.brand       } : {}),
        ...(data.finish ? { finish: data.finish } : {}),
        ...(data.painted_at && data.painted_at !== '' ? { painted_at: data.painted_at } : {}),
        ...(data.notes       ? { notes:       data.notes       } : {}),
      };
      if (existing) {
        await api.put(`/paint-colors/${existing.id}`, payload);
      } else {
        await api.post(`/homes/${homeId}/paint-colors`, payload);
      }
      onSuccess();
    } catch (err) {
      setError('root', { message: err instanceof Error ? err.message : 'Failed to save.' });
    }
  };

  const inp = 'w-full px-4 py-3 rounded-xl border border-gray-200 text-sm bg-white outline-none focus:border-blue-500';

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end" onClick={onClose}>
      <div className="w-full bg-white rounded-t-3xl p-5 max-h-[90dvh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold text-gray-900">{existing ? 'Edit' : 'Add'} Paint Color</h2>
          <button onClick={onClose} className="text-gray-400 text-2xl p-1" style={{ minHeight: 44 }}>✕</button>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Room *</label>
            <input {...register('room')} placeholder="e.g. Living Room" className={inp} />
            {errors.room && <p className="text-red-500 text-xs mt-1">{errors.room.message}</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Color name</label>
              <input {...register('color_name')} placeholder="e.g. Agreeable Gray" className={inp} />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Hex color code</label>
              <div className="relative">
                <input {...register('color_code')} placeholder="#d1c9b8" className={inp + ' pr-12'} />
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Brand</label>
              <input {...register('brand')} placeholder="e.g. Sherwin-Williams" className={inp} />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Finish</label>
              <select {...register('finish')} className={inp}>
                <option value="">Select…</option>
                {FINISH_OPTS.map(f => (
                  <option key={f} value={f}>{f.replace(/_/g,' ')}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs text-gray-500 mb-1 block">Date painted</label>
            <input {...register('painted_at')} type="date" className={inp} />
          </div>

          <div>
            <label className="text-xs text-gray-500 mb-1 block">Notes</label>
            <textarea {...register('notes')} rows={2} placeholder="Store location, color number, etc." className={inp + ' resize-none'} />
          </div>

          {errors.root && (
            <div className="bg-red-50 rounded-xl p-3 text-red-600 text-sm">{errors.root.message}</div>
          )}

          <button type="submit" disabled={isSubmitting} className="w-full py-3.5 bg-blue-600 text-white rounded-xl font-semibold disabled:opacity-60">
            {isSubmitting ? 'Saving…' : (existing ? 'Save changes' : 'Add color')}
          </button>
        </form>
      </div>
    </div>
  );
}
